import { QuantitySelectorComponent } from '@theme/component-quantity-selector';
import { QuantitySelectorUpdateEvent } from '@theme/events';

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * @extends {QuantitySelectorComponent}
 */
class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max; // Cart uses absolute max, not max minus cart quantity
  }

  /**
   * Overrides base decreaseQuantity to allow going to 0, which triggers line-item removal.
   * The base class clamps to data-min (1), so it never fires an event with quantity=0.
   */
  decreaseQuantity() {
    const { quantityInput } = this.refs;
    if (!quantityInput) return;
    const currentValue = parseInt(quantityInput.value, 10) || 0;
    const newValue = Math.max(0, currentValue - 1);
    if (newValue === currentValue) return;
    quantityInput.value = newValue;
    const cartLine = parseInt(quantityInput.dataset.cartLine, 10);
    this.dispatchEvent(new QuantitySelectorUpdateEvent(newValue, cartLine));
    this.updateButtonStates();
  }

  /**
   * Updates button states based on current value and limits.
   * Cart buttons are always managed client-side, never server-disabled.
   * Minus is enabled down to 0 (quantity=0 removes the item).
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    minusButton.disabled = value <= 0;
    plusButton.disabled = effectiveMax !== null && value >= effectiveMax;
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
